import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { isForecastEnabled, isXeroEnabled } from '@/lib/feature-flags';
import { FinanceGuard } from '@/components/finance/FinanceGuard';
import { FinanceNav } from '@/components/tracking/FinanceNav';
import { FinanceSlider } from '@/components/finance/FinanceSlider';
import { useFinancialModel } from '@/hooks/use-financial-model';
import { overheadRecoveryRate, latestMetricValue } from '@/lib/financial-calc/forecast';
import { fmtCurrency } from '@/components/tracking/format';
import type { AccountRole } from '@/types/tracking-view';
import { ForecastToolNav } from '@/components/finance/ForecastToolNav';

/**
 * /finance/forecast/overhead-recovery — the Overhead Recovery tool (F-013 S6.5). The $/hr to add to
 * base labour to cover overheads, plus a charge-out rate that also clears a target margin. Overhead
 * from the real model; hours + base rate are your inputs. Real + flag-gated.
 */
export const Route = createFileRoute('/finance/forecast_/overhead-recovery')({
  beforeLoad: () => {
    if (!isXeroEnabled() || !isForecastEnabled()) {
      throw redirect({ to: '/finance', replace: true });
    }
  },
  component: () => (
    <FinanceGuard>
      <OverheadRecovery />
    </FinanceGuard>
  ),
});


function OverheadRecovery() {
  const { model, input } = useFinancialModel();
  const periods = input?.periods ?? [];
  const cur = periods[periods.length - 1]?.roleTotals ?? {};
  const n = (k: AccountRole) => cur[k] ?? 0;
  const overhead = n('overhead');
  const recovery = model?.metricsById['overhead-recovery']?.value ?? null;

  const [hours, setHours] = useState(900);
  const [baseRate, setBaseRate] = useState(55);
  // Follows the model until touched — a plain useState captured the empty first
  // paint before the model loaded (F-124 review #6, second sweep).
  const [marginOv, setMarginOv] = useState<number | null>(null);
  const margin = marginOv ?? Math.round(latestMetricValue(model?.metricsById['gross-margin']) ?? 30);
  const setMargin = setMarginOv;

  const r = overheadRecoveryRate(overhead, hours, baseRate, margin);

  const Stat = ({ l, v, note }: { l: string; v: string; note?: string }) => (
    <div className="rounded-2xl border border-border bg-card p-4">
      <p className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">{l}</p>
      <p className="mt-1 ds-kpi-md text-foreground">{v}</p>
      {note && <p className="mt-1 text-[11.5px] text-muted-foreground">{note}</p>}
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-6xl">
      <FinanceNav />
      <Link to="/finance/forecast" className="mt-2 inline-flex items-center gap-1 text-[12px] text-muted-foreground no-underline hover:text-foreground">
        <ChevronLeft className="h-3.5 w-3.5" /> Forecast
      </Link>
      <h1 className="ds-h1 mt-1">Overhead Recovery</h1>
      <p className="mb-4 mt-0.5 text-[13px] text-muted-foreground">The hourly uplift your rates need so overheads are covered — not eaten out of profit.</p>

      {!model || periods.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">
          <p className="font-semibold text-foreground">Needs your Xero data</p>
          <p className="mt-1 text-[12.5px]">Connect Xero and sync so we know your overheads.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-3">
          <div className="rounded-2xl border border-border bg-card p-4">
            <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">Inputs</p>
            <div className="mb-3 rounded-lg bg-muted/40 px-2.5 py-1.5 text-[12px] text-muted-foreground">Overheads (from Xero): <b className="text-foreground">{fmtCurrency(overhead)}/mo</b>{recovery != null && <> · currently recovering <b className="text-foreground">{recovery.toFixed(2)}×</b></>}</div>
            <div className="flex flex-col gap-2.5">
              <FinanceSlider label="Billable hours / mo" value={hours} set={setHours} min={100} max={3_000} step={10} fmt={(v) => `${v} h`} />
              <FinanceSlider label="Base labour cost ($/hr)" value={baseRate} set={setBaseRate} min={20} max={150} step={1} fmt={(v) => `$${v}`} />
              <FinanceSlider label="Target gross margin" value={margin} set={setMargin} min={5} max={50} step={1} fmt={(v) => `${v}%`} />
              {(() => {
                const total = r.recommendedChargeOut || 1;
                const seg = (v: number) => `${Math.max(0, (v / total) * 100)}%`;
                const marginSlice = r.recommendedChargeOut - r.costPlusOverhead;
                return (
                  <div className="mt-1 border-t border-border pt-2.5">
                    <p className="mb-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">What’s inside the charge-out rate</p>
                    <div className="flex h-4 overflow-hidden rounded">
                      <div className="bg-[hsl(var(--info))]" style={{ width: seg(baseRate) }} title="Base labour" />
                      <div className="bg-warning/70" style={{ width: seg(r.overheadPerHour) }} title="Overhead recovery" />
                      <div className="bg-success/70" style={{ width: seg(marginSlice) }} title="Margin" />
                    </div>
                    <p className="mt-1 text-[10.5px] text-muted-foreground">Labour {fmtCurrency(baseRate)} · overheads {fmtCurrency(r.overheadPerHour)} · margin {fmtCurrency(marginSlice)} — every billable hour must carry all three.</p>
                  </div>
                );
              })()}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:col-span-2 lg:grid-cols-3">
            <Stat l="Overhead / hour" v={`${fmtCurrency(r.overheadPerHour)}`} note="add this to base labour just to cover overheads" />
            <Stat l="Cost + overhead" v={`${fmtCurrency(r.costPlusOverhead)}/hr`} note="your true break-even hourly cost" />
            <Stat l="Recommended charge-out" v={`${fmtCurrency(r.recommendedChargeOut)}/hr`} note={`covers overheads AND a ${margin}% margin`} />
          </div>
        </div>
      )}
      <p className="mt-4 text-[11px] text-muted-foreground">A calculator — overheads come from your real Xero data; hours and base rate are yours to set. Feed the recommended rate into Project Pricing.</p>
      <ForecastToolNav current="overhead-recovery" />
    </div>
  );
}
